import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useProjects } from "@/hooks/useProjects";
import { useUpdateTimeEntry } from "@/hooks/useTimeEntries";
import { useToast } from "@/hooks/use-toast";
import { CategorySelect } from "@/components/timer/CategorySelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Database } from "@/integrations/supabase/types";

type TimeCategory = Database["public"]["Enums"]["time_category"];

interface TimeEntryData {
  id: string;
  project_id: string | null;
  category: TimeCategory | null;
  custom_category?: string | null;
  description: string | null;
  start_time: string;
  end_time: string | null;
  is_billable: boolean | null;
  hourly_rate: number | null;
}

interface EditTimeEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: TimeEntryData | null;
}

const categories: { id: TimeCategory; name: string }[] = [
  { id: "dev", name: "Desenvolvimento" },
  { id: "meeting", name: "Reunião" },
  { id: "research", name: "Pesquisa" },
  { id: "admin", name: "Administrativo" },
];

export function EditTimeEntryDialog({ open, onOpenChange, entry }: EditTimeEntryDialogProps) {
  const { toast } = useToast();
  const { data: projects } = useProjects();
  const updateTimeEntry = useUpdateTimeEntry();

  const [projectId, setProjectId] = useState<string>("");
  const [category, setCategory] = useState<TimeCategory>("dev");
  const [customCategory, setCustomCategory] = useState<string>("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [isBillable, setIsBillable] = useState(true);
  const [hourlyRate, setHourlyRate] = useState("");

  useEffect(() => {
    if (entry) {
      setProjectId(entry.project_id || "");
      setCategory(entry.category || "dev");
      setCustomCategory(entry.custom_category || "");
      setDescription(entry.description || "");
      setDate(new Date(entry.start_time));
      setStartTime(format(new Date(entry.start_time), "HH:mm"));
      setEndTime(entry.end_time ? format(new Date(entry.end_time), "HH:mm") : "");
      setIsBillable(entry.is_billable ?? true);
      setHourlyRate(entry.hourly_rate?.toString() || "");
    }
  }, [entry]);

  const handleSave = async () => {
    if (!entry) return;

    // Validar horário de início
    if (!startTime) {
      toast({ variant: "destructive", title: "Erro", description: "Informe o horário de início." });
      return;
    }

    // Validar hourlyRate (NaN check)
    if (hourlyRate && (isNaN(parseFloat(hourlyRate)) || parseFloat(hourlyRate) < 0)) {
      toast({ variant: "destructive", title: "Erro", description: "Taxa por hora deve ser um valor válido." });
      return;
    }

    const dateStr = format(date, "yyyy-MM-dd");
    const newStartTime = new Date(`${dateStr}T${startTime}:00`);
    const newEndTime = endTime ? new Date(`${dateStr}T${endTime}:00`) : null;

    // Validar que as datas criadas são válidas
    if (isNaN(newStartTime.getTime())) {
      toast({ variant: "destructive", title: "Erro", description: "Horário de início inválido." });
      return;
    }

    if (newEndTime && isNaN(newEndTime.getTime())) {
      toast({ variant: "destructive", title: "Erro", description: "Horário de fim inválido." });
      return;
    }

    if (newEndTime && newEndTime <= newStartTime) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "O horário de fim deve ser maior que o de início.",
      });
      return;
    }

    const durationMinutes = newEndTime 
      ? Math.round((newEndTime.getTime() - newStartTime.getTime()) / 60000)
      : null;

    if (durationMinutes && durationMinutes > 24 * 60) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Duração máxima de 24 horas por entrada.",
      });
      return;
    }

    try {
      await updateTimeEntry.mutateAsync({
        id: entry.id,
        project_id: projectId || null,
        category,
        custom_category: customCategory.trim() || null,
        description: description.trim() || null,
        start_time: newStartTime.toISOString(),
        end_time: newEndTime?.toISOString() || null,
        duration_minutes: durationMinutes,
        is_billable: isBillable,
        hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
      });
      toast({
        title: "Entrada atualizada!",
        description: "As alterações foram salvas.",
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar a entrada.",
      });
    }
  };

  // Memoiza cálculo da duração para evitar recálculo a cada render
  const displayDuration = useMemo(() => {
    if (!startTime || !endTime) return "-";
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) return "-";
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;
    if (endMins <= startMins) return "-";
    const diff = endMins - startMins;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  }, [startTime, endTime]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Entrada</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Projeto</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um projeto" />
              </SelectTrigger>
              <SelectContent>
                {projects?.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Categoria</Label>
            <CategorySelect
              value={category}
              customCategory={customCategory}
              onCategoryChange={setCategory}
              onCustomCategoryChange={setCustomCategory}
            />
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="O que você fez?"
            />
          </div>

          <div className="space-y-2">
            <Label>Data</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2">
                  <CalendarDays className="h-4 w-4" />
                  {format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  locale={ptBR}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Início</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Fim</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Duração: <strong>{displayDuration}</strong>
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="billable"
                checked={isBillable}
                onCheckedChange={(checked) => setIsBillable(checked as boolean)}
              />
              <Label htmlFor="billable" className="cursor-pointer">Faturável</Label>
            </div>
            <div className="space-y-2">
              <Label>Taxa/Hora (R$)</Label>
              <Input
                type="number"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder="0,00"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={updateTimeEntry.isPending}>
            {updateTimeEntry.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
